import { makeValuesPredicate } from '@sgme/fp';

import type { BlotterEntryStatus } from '@/store/blotter/api/types/BlotterEntryStatus.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';
import type { Rate } from '@/utils/rates.ts';

export type BlotterTradeEntry = {
  //
  // technical fields and discriminants
  //
  id: string;
  type: 'BLOTTER.TRADE';
  blotterType: BlotterTradeInstrument;
  product: BlotterProductType;
  status: BlotterEntryStatus;
  date: ISODateTime; // "exec time" or "trade date" in legecy fx, depending on the context ...

  updateTime: ISODateTime;

  //
  // who
  //
  venue: string; // ex : 'SG-MARKETS'
  portfolio: string;
  counterpartyBdrId: number;
  account: string;
  accountLongName: string;
  electronicAccount: string | null; // user having created the transaction
  electronicReference: string | null;
  salesFullName: string;
  user: null;

  //
  //
  // what
  //
  currencyPair: CurrencyPair;
  dealtAmount: number;
  dealtCurrency: Currency;

  spotDate: ISODate | null;
  expiry: ISODate;
  spot: string; // rate
  points: string | null;
  way: 'Sell' | 'Buy' | 'Both';

  //
  // Swap only ?
  //
  contraAmount: string | null;
  contraCurrency: Currency;
  farContraAmount: string | null;
  farDate: ISODate | null;
  farDealtAmount: string | null;
  farRate: Rate | null;
  nearDate: ISODate | null;
  nearRate: Rate | null;

  //
  // Specific to Options
  //
  optionType: BlotterTradeOptionType | null;
  strike: string | null;
  strategyReference: string | null; // if not null : points to another option id (the entry is then a lag of another entry, which is the strategy)
  forwardRate: string | null;
  premium: string | null;
  premiumCurrency:
    | Currency
    | null
    // empty string: observed on uat
    | '';
  premiumSide:
    | 'Pay'
    | 'Receive'
    // empty string: observed on uat
    | ''
    | null;
  deliveryDate: ISODateTime | null;
  deliveryType: string | 'Physical_Spot' | null;
  premiumPaymentDate: ISODate | null;
  cutOffPlace: string | null;
  callabilityStartDate: ISODate | null; // also american forward ?

  //
  // Specific to american forward (seems to be)
  //
  // cf https://sgithub.fr.world.socgen/SGMARKETS-EXECUTION/fx/blob/main/fx-web/app/src/api/multipass/americanForward/americanForwardQuoteRequestBuilder.ts#L163
  // cf https://sgithub.fr.world.socgen/SGMARKETS-EXECUTION/fx/blob/0758273c0530217ab64b63d51f4f3f469a219263/fx-web/app/src/api/multipass/americanForward/americanForwardQuoteRequestBuilder.ts#L124
  callabilityStyle: 'Windows' | 'American' | null;

  //
  // Specific to Accumulator
  //
  fixingFrequency: string | null;
  settlementFrequency: string | null;
  koConventionType: string | null;
  koTime: ISODateTime | null;
};

//
// initially borrowed from legacy fx: blotterEntryModel.ts
//
export const cashTradeTypes = [
  'Spot',
  'Outright', // "Forward" in the rest of the app
  'Ndf',
] as const;
export const swapTradeTypes = ['Swap', 'Nds'] as const;
export const optionSingleOrCompoundTradeTypes = [
  'Vanilla',
  'Strategy',
  'Straddle',
  'Strangle',
  'RiskReversal',
  'AmericanForward',
  // these has been observed in production
  // These options are not generated by FX but still received by the blotter
  'Spot',
  'Ndf', // NDO
] as const;
export const optionLegTradeTypes = [
  // all product types of option trades observed to be leg of another option trade
  'Vanilla',
  'Outright',
  'Ndf',
  'Spot',
] as const;
export const accumulatorTypes = ['TargetAccumulator', 'ForwardAccumulator'] as const;
export const tradeTypes = [
  ...cashTradeTypes,
  ...swapTradeTypes,
  ...optionSingleOrCompoundTradeTypes,
  ...accumulatorTypes,
] as const;

export type BlotterCashProductType = (typeof cashTradeTypes)[number];
export type BlotterSwapProductType = (typeof swapTradeTypes)[number];
export type BlotterSingleOrCompoundOptionProductType = (typeof optionSingleOrCompoundTradeTypes)[number];
export type BlotterLegOptionProductType = (typeof optionLegTradeTypes)[number];
export type BlotterAccumulatorProductType = (typeof accumulatorTypes)[number];

export const isCashProductType = makeValuesPredicate(cashTradeTypes);
export const isSwapProductType = makeValuesPredicate(swapTradeTypes);
export const isOptionProductType = makeValuesPredicate(optionSingleOrCompoundTradeTypes);
export const isOptionLegProductType = makeValuesPredicate(optionLegTradeTypes);
export const isAccumulatorProductType = makeValuesPredicate(accumulatorTypes);

export type BlotterProductType = (typeof tradeTypes)[number];

export const blotterTradeInstruments = ['Cash', 'Option', 'Accumulator'] as const;
export type BlotterTradeInstrument = (typeof blotterTradeInstruments)[number];

export const isBlotterTradeInstrument = makeValuesPredicate(blotterTradeInstruments);

export type BlotterTradeOptionType =
  | 'CE'
  | 'Call'
  | 'PE'
  | 'Put'
  | 'CECSD1'
  | 'PECSD1'
  | 'PEFWD'
  | 'CEFWD'
  | 'PECSD2'
  | 'CECSD2'
  | 'STRAT'
  | 'STRATNET'
  | 'FXOTargetAccumulator';
